import React, { useContext, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    FormControlLabel,
    Switch,
    Paper,
    Box,
    Typography,
    Avatar,
    Chip,
} from '@material-ui/core';
import FeedbacksContext from '../contexts/FeedbacksContext';
import { brand } from '../theme';

const useStyles = makeStyles((theme) => ({
    header: {
        marginBottom: theme.spacing(3),
    },
    muted: {
        color: theme.palette.text.secondary,
    },
    list: {
        display: 'grid',
        gap: theme.spacing(2),
    },
    card: {
        padding: theme.spacing(2.5, 3),
        border: `1px solid ${brand.line}`,
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(2),
        },
    },
    top: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
    },
    avatar: {
        width: 36,
        height: 36,
        backgroundColor: brand.ink,
        fontSize: '0.95rem',
        fontWeight: 600,
    },
    name: {
        fontWeight: 600,
        flexGrow: 1,
        minWidth: 0,
        overflowWrap: 'anywhere',
    },
    chipVisible: {
        backgroundColor: 'rgba(46,125,50,0.12)',
        color: '#2E7D32',
        fontWeight: 600,
    },
    chipHidden: {
        backgroundColor: 'rgba(92,103,115,0.12)',
        color: brand.muted,
        fontWeight: 600,
    },
    text: {
        whiteSpace: 'pre-line',
        overflowWrap: 'anywhere',
        marginBottom: theme.spacing(1),
    },
    empty: {
        padding: theme.spacing(5, 3),
        textAlign: 'center',
        border: `1px dashed ${brand.line}`,
        color: theme.palette.text.secondary,
    },
}));

export default function AdminFeedbacks() {
    const classes = useStyles();
    const feedbacksContext = useContext(FeedbacksContext);
    const { feedbacks, updateFeedback } = feedbacksContext;
    const [localFeedbacks, setLocalFeedbacks] = useState([]);

    useEffect(() => {
        setLocalFeedbacks(feedbacks);
    }, [feedbacks]);

    const handleChange = (event, id) => {
        const copiedFeedbacks = [...localFeedbacks];
        const feedBackToUpdateIndex = copiedFeedbacks.findIndex(item => item.id === id);
        copiedFeedbacks[feedBackToUpdateIndex].show = event.target.checked;
        updateFeedback(copiedFeedbacks[feedBackToUpdateIndex]);
        setLocalFeedbacks(copiedFeedbacks);
    };

    const visibleCount = localFeedbacks.filter((item) => item.show).length;

    return (
        <>
            <Box className={classes.header}>
                <Typography variant="h5" component="h2">Customer reviews</Typography>
                <Typography variant="body2" className={classes.muted}>
                    {localFeedbacks.length} review{localFeedbacks.length === 1 ? '' : 's'}, {visibleCount} shown on the site.
                    New reviews stay hidden until you turn them on.
                </Typography>
            </Box>

            {localFeedbacks.length === 0 ? (
                <Paper className={classes.empty}>
                    <Typography>No reviews yet.</Typography>
                </Paper>
            ) : (
                <div className={classes.list}>
                    {localFeedbacks.map((value, index) => (
                        <Paper key={value.id || index} className={classes.card}>
                            <div className={classes.top}>
                                <Avatar className={classes.avatar}>
                                    {(value.feedbackName || '?').trim().charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography className={classes.name}>{value.feedbackName}</Typography>
                                <Chip
                                    size="small"
                                    label={value.show ? 'Visible' : 'Hidden'}
                                    className={value.show ? classes.chipVisible : classes.chipHidden}
                                />
                            </div>
                            <Typography variant="body2" className={classes.text}>
                                "{value.feedback}"
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!value.show}
                                        onChange={(event) => handleChange(event, value.id)}
                                        name={`show-${value.id}`}
                                        color="primary"
                                    />
                                }
                                label="Show on website"
                            />
                        </Paper>
                    ))}
                </div>
            )}
        </>
    );
}
